/**
 * Llamadas del backoffice (epica ADM).
 */
import type {
  ActualizarContenidoProductoDto,
  ActualizarSeoDto,
  CrearProductoDto,
  DatosProductoDto,
  EdicionMasivaProductosDto,
  FiltrosMovimientosDto,
  FiltrosPedidosDto,
  FiltrosProductosAdminDto,
  ImagenProducto,
  MovimientoStock,
  MovimientoStockDto,
  OpcionesFiltroProductos,
  Paginado,
  PanelAdministracion,
  Pedido,
  PedidoAdminDetalle,
  ProductoAdminDetalle,
  ProductoAdminResumen,
  RespuestaEdicionMasiva,
  RespuestaProductoCreado,
  ResumenInventario,
  TerminoSinResultado,
  VideoProducto,
} from '@bandu/compartido';

import { pedir } from './cliente-api';

export function obtenerPanel(senal?: AbortSignal): Promise<PanelAdministracion> {
  return pedir<PanelAdministracion>('/admin/panel', { ...(senal ? { senal } : {}) });
}

export function obtenerProductos(
  filtros: FiltrosProductosAdminDto,
  senal?: AbortSignal,
): Promise<Paginado<ProductoAdminResumen>> {
  // Los nombres de la query son los del backend (`consultarProductosAdmin`):
  // en singular las listas, porque cada valor viaja como un parametro repetido.
  return pedir<Paginado<ProductoAdminResumen>>('/admin/productos', {
    parametros: {
      pagina: filtros.pagina,
      porPagina: filtros.porPagina,
      termino: filtros.termino,
      linea: filtros.lineas,
      categoria: filtros.categorias,
      marca: filtros.marcas,
      visibilidad: filtros.visibilidad,
      conFoto: filtros.conFoto,
      conPrecio: filtros.conPrecio,
      stockMin: filtros.stockMin,
      stockMax: filtros.stockMax,
      soloDestacados: filtros.soloDestacados,
      orden: filtros.orden,
    },
    ...(senal ? { senal } : {}),
  });
}

/** Lineas, categorias y marcas que ofrecen los filtros de la tabla (ADM-02). */
export function obtenerOpcionesFiltroProductos(senal?: AbortSignal): Promise<OpcionesFiltroProductos> {
  return pedir<OpcionesFiltroProductos>('/admin/productos/opciones-filtro', {
    ...(senal ? { senal } : {}),
  });
}

/** Detalle completo de un producto para su pantalla propia (ADM-02). */
export function obtenerProductoAdmin(id: number, senal?: AbortSignal): Promise<ProductoAdminDetalle> {
  return pedir<ProductoAdminDetalle>(`/admin/productos/${id}`, { ...(senal ? { senal } : {}) });
}

/** Alta de un producto (ADM-02). Nace oculto: se completa y se publica desde su detalle. */
export function crearProducto(datos: CrearProductoDto): Promise<RespuestaProductoCreado> {
  return pedir<RespuestaProductoCreado>('/admin/productos', { metodo: 'POST', cuerpo: datos });
}

/** Nombre, codigo, precio, categoria y variante. El stock no: ese va por el inventario. */
export function actualizarProducto(id: number, datos: DatosProductoDto): Promise<void> {
  return pedir<void>(`/admin/productos/${id}`, { metodo: 'PUT', cuerpo: datos });
}

/** Publicar/ocultar un producto desde su pantalla de detalle (ADM-02). */
export function actualizarVisibilidadProducto(id: number, visible: boolean): Promise<void> {
  return pedir<void>(`/admin/productos/${id}/visible`, { metodo: 'PATCH', cuerpo: { visible } });
}

/** Descripcion y ficha tecnica (ADM-02). */
export function actualizarContenidoProducto(
  id: number,
  datos: ActualizarContenidoProductoDto,
): Promise<void> {
  return pedir<void>(`/admin/productos/${id}/contenido`, { metodo: 'PATCH', cuerpo: datos });
}

/**
 * Cambia categoria y/o visibilidad de varios productos a la vez (ADM-03).
 * Precio y stock no viajan aca: el precio se cambia mirando el producto, y el
 * stock solo con un movimiento de inventario.
 */
export function actualizarProductosMasivo(
  datos: EdicionMasivaProductosDto,
): Promise<RespuestaEdicionMasiva> {
  return pedir<RespuestaEdicionMasiva>('/admin/productos/masivo', { metodo: 'PATCH', cuerpo: datos });
}

/**
 * Sube una foto al producto (ADM-04).
 *
 * El archivo viaja como bytes crudos con su propio tipo, sin `multipart` y sin
 * envolverlo en un JSON —que lo haria un tercio mas pesado al pasarlo a
 * base64—. Es el mismo camino que ya usa la busqueda por foto.
 *
 * El navegador NO mide ni achica nada antes de mandar: el archivo que se
 * publica no es el que se eligio (el servidor lo endereza, lo achica y lo
 * convierte a WebP), asi que medirlo aca daria las medidas de algo que nadie va
 * a descargar. Las que valen vienen en la respuesta.
 */
export function subirFotoProducto(id: number, archivo: File): Promise<ImagenProducto> {
  return pedir<ImagenProducto>(`/admin/productos/${id}/fotos`, {
    metodo: 'POST',
    cuerpoBinario: archivo,
  });
}

/** Quita una foto del producto, del catalogo y del almacen (ADM-04). */
export function eliminarFotoProducto(id: number, imagenId: number): Promise<void> {
  return pedir<void>(`/admin/productos/${id}/fotos/${imagenId}`, { metodo: 'DELETE' });
}

/**
 * Texto alternativo de una foto (ADM-04). Vacio se guarda como `null`: sin
 * texto propio, la ficha usa el nombre del producto.
 */
export function describirFotoProducto(
  id: number,
  imagenId: number,
  textoAlternativo: string | null,
): Promise<ImagenProducto> {
  return pedir<ImagenProducto>(`/admin/productos/${id}/fotos/${imagenId}`, {
    metodo: 'PATCH',
    cuerpo: { textoAlternativo },
  });
}

/**
 * Sube o baja una foto un lugar. Devuelve la galeria completa: mover cambia dos
 * fotos, no una. La primera es la principal.
 */
export function moverFotoProducto(
  id: number,
  imagenId: number,
  direccion: 'subir' | 'bajar',
): Promise<ImagenProducto[]> {
  return pedir<ImagenProducto[]>(`/admin/productos/${id}/fotos/${imagenId}/mover`, {
    metodo: 'POST',
    cuerpo: { direccion },
  });
}

/**
 * Agrega un video a la ficha, al final de los que tenga. Viaja crudo como las
 * fotos, pero el servidor no lo convierte: tiene que llegar ya comprimido.
 */
export function subirVideoProducto(id: number, archivo: File): Promise<VideoProducto> {
  return pedir<VideoProducto>(`/admin/productos/${id}/videos`, {
    metodo: 'POST',
    cuerpoBinario: archivo,
  });
}

export function eliminarVideoProducto(id: number, videoId: number): Promise<void> {
  return pedir<void>(`/admin/productos/${id}/videos/${videoId}`, { metodo: 'DELETE' });
}

/** Eliminar un producto (ADM-02). No tiene vuelta atras. */
export function eliminarProducto(id: number): Promise<void> {
  return pedir<void>(`/admin/productos/${id}`, { metodo: 'DELETE' });
}

export function obtenerPedidos(
  filtros: FiltrosPedidosDto,
  senal?: AbortSignal,
): Promise<Paginado<Pedido>> {
  // Los nombres de la query son los del backend (`consultarPedidos`).
  return pedir<Paginado<Pedido>>('/admin/pedidos', {
    parametros: {
      pagina: filtros.pagina,
      porPagina: filtros.porPagina,
      termino: filtros.termino,
      estado: filtros.estado,
      documento: filtros.documento,
      tipoEntrega: filtros.tipoEntrega,
      tipoDocumento: filtros.tipoDocumento,
      desde: filtros.desde,
      hasta: filtros.hasta,
      totalMin: filtros.totalMin,
      totalMax: filtros.totalMax,
      orden: filtros.orden,
    },
    ...(senal ? { senal } : {}),
  });
}

/** Todo lo que se sabe de un pedido: el modal del historial (ADM-09). */
export function obtenerPedidoAdmin(id: number, senal?: AbortSignal): Promise<PedidoAdminDetalle> {
  return pedir<PedidoAdminDetalle>(`/admin/pedidos/${id}`, senal ? { senal } : {});
}

/** Titulo y descripcion SEO de un producto (ADM-08). */
export function actualizarSeoProducto(id: number, datos: ActualizarSeoDto): Promise<void> {
  return pedir<void>(`/admin/productos/${id}/seo`, { metodo: 'PATCH', cuerpo: datos });
}

export function obtenerBusquedasSinResultado(
  opciones: { pagina?: number; senal?: AbortSignal } = {},
): Promise<Paginado<TerminoSinResultado>> {
  return pedir<Paginado<TerminoSinResultado>>('/admin/busquedas', {
    parametros: { pagina: opciones.pagina },
    ...(opciones.senal ? { senal: opciones.senal } : {}),
  });
}

/**
 * Sube la boleta o factura de un pedido (DOC-01). El PDF viaja como bytes
 * crudos, igual que las fotos, y el folio en la consulta: un cuerpo crudo no
 * deja lugar para otro campo. Al guardarse, el servidor se lo manda por correo
 * al comprador.
 */
export function subirDocumentoPedido(id: number, folio: string, archivo: File): Promise<Pedido> {
  return pedir<Pedido>(`/admin/pedidos/${id}/documento`, {
    metodo: 'POST',
    parametros: { folio },
    cuerpoBinario: archivo,
  });
}

/* -------------------------------------------------------------------------
   Inventario (INV-01, INV-02)
   ------------------------------------------------------------------------- */

export function obtenerMovimientos(
  filtros: FiltrosMovimientosDto,
  senal?: AbortSignal,
): Promise<Paginado<MovimientoStock>> {
  return pedir<Paginado<MovimientoStock>>('/admin/inventario/movimientos', {
    parametros: {
      pagina: filtros.pagina,
      porPagina: filtros.porPagina,
      productoId: filtros.productoId,
      termino: filtros.termino,
      tipo: filtros.tipo,
      desde: filtros.desde,
      hasta: filtros.hasta,
    },
    ...(senal ? { senal } : {}),
  });
}

/** Los contadores de arriba de Inventario, que tambien filtran (INV-01). */
export function obtenerResumenInventario(senal?: AbortSignal): Promise<ResumenInventario> {
  return pedir<ResumenInventario>('/admin/inventario/resumen', senal ? { senal } : {});
}

/** Anota un ingreso, una venta por fuera o un ajuste. Es la unica forma de cambiar el stock. */
export function registrarMovimiento(datos: MovimientoStockDto): Promise<MovimientoStock> {
  return pedir<MovimientoStock>('/admin/inventario/movimientos', { metodo: 'POST', cuerpo: datos });
}
